import { meApi } from '@/lib/api';
import { Tabs } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { DisplayTab } from './display-tab';
import { SettingsForbidden } from './settings-forbidden';
import { SystemTab } from './system-tab';

export type SettingsTab = 'affichage' | 'systeme';

interface SettingsPageProps {
  tab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
}

function isSettingsTab(value: string): value is SettingsTab {
  return value === 'affichage' || value === 'systeme';
}

/**
 * The Réglages page. Access is decided by the REAL role (`user.role`), never by
 * `useEffectiveRole`: the page hosts the role simulation, so it must not
 * lock its own user out while one is active. `pms_user` and `pms_admin` get the
 * tabs; the client roles get a "réservée" state (the tweaks have no backend to
 * answer 403). The active tab lives in the URL (`?tab=`), owned by the route.
 */
export function SettingsPage({ tab, onTabChange }: SettingsPageProps) {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => meApi.get() });
  const role = meQuery.data?.role;

  return (
    <section className="max-w-[1000px] space-y-6">
      <h1 className="font-bold text-bleu-nuit text-h2">Réglages</h1>
      {meQuery.isPending ? (
        <p className="text-gray-600 text-small">Chargement…</p>
      ) : role === 'pms_user' || role === 'pms_admin' ? (
        <Tabs
          label="Sections des réglages"
          value={tab}
          onValueChange={(next) => {
            if (isSettingsTab(next)) onTabChange(next);
          }}
          items={[
            { value: 'affichage', label: 'Affichage', content: <DisplayTab /> },
            {
              value: 'systeme',
              label: 'Génération & système',
              content: <SystemTab realRole={role} />,
            },
          ]}
        />
      ) : (
        <SettingsForbidden />
      )}
    </section>
  );
}
